import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CardMedia,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Helmet } from 'react-helmet-async';
import StrapiBlocksRenderer from './StrapiBlocksRenderer';
import { fetchArticleBySlug, formatArticleDate, getArticleUrl } from '../services/strapiBlog';

const defaultCover = '/images/symuliga.webp';

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError(null);

    fetchArticleBySlug(slug)
      .then((result) => {
        if (active) setArticle(result);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError.status === 404 ? 'Artykuł nie został znaleziony.' : 'Nie udało się pobrać artykułu z CMS.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  const seo = useMemo(() => {
    if (!article) return null;
    const title = `${article.title} | SymuLiga.pl`;
    const description = article.description || 'Artykuł na blogu SymuLiga.pl.';
    const url = getArticleUrl(article.slug);
    const image = article.cover?.url || `${window.location.origin}${defaultCover}`;

    return { title, description, url, image };
  }, [article]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="warning" />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box sx={{ maxWidth: 860, mx: 'auto', px: 2, py: 5 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Artykuł nie został znaleziony.'}
        </Alert>
        <Button component={Link} to="/blog" startIcon={<ArrowBackIcon />}>
          Wróć do bloga
        </Button>
      </Box>
    );
  }

  return (
    <>
      {seo && (
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          <meta name="author" content={article.author.name} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={seo.url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:url" content={seo.url} />
          <meta property="og:image" content={seo.image} />
          <meta name="twitter:card" content="summary_large_image" />
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: seo.description,
              image: [seo.image],
              datePublished: article.publishedAt,
              dateModified: article.updatedAt || article.publishedAt,
              author: { '@type': 'Person', name: article.author.name },
              publisher: { '@type': 'Organization', name: 'SymuLiga.pl' },
              mainEntityOfPage: seo.url,
            })}
          </script>
        </Helmet>
      )}

      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: { xs: 1, md: 2 }, py: { xs: 2, md: 4 } }}>
        <Button component={Link} to="/blog" startIcon={<ArrowBackIcon />} sx={{ mb: 2, color: '#FFD700' }}>
          Blog
        </Button>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {article.category?.name && <Chip label={article.category.name} color="warning" size="small" />}
          {article.publishedAt && <Chip label={formatArticleDate(article.publishedAt)} size="small" variant="outlined" />}
          <Chip
            avatar={
              article.author.avatar?.url ? (
                <Avatar src={article.author.avatar.url} alt={article.author.avatar.alternativeText || article.author.name} />
              ) : undefined
            }
            label={`Autor: ${article.author.name}`}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Typography variant="h4" component="h1" sx={{ mb: 2, lineHeight: 1.18 }}>
          {article.title}
        </Typography>

        <CardMedia
          component="img"
          image={article.cover?.url || defaultCover}
          alt={article.cover?.alternativeText || article.title}
          sx={{
            width: '100%',
            maxWidth: 760,
            maxHeight: 420,
            objectFit: 'contain',
            borderRadius: '8px',
            mx: 'auto',
            mb: 3,
            backgroundColor: '#1a1a1a',
          }}
        />

        <Paper
          component="article"
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: '#2c2c2c',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px',
          }}
        >
          <StrapiBlocksRenderer blocks={article.blocks} />
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
          <Button component={Link} to="/blog" variant="outlined" startIcon={<ArrowBackIcon />} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>
            Wróć do bloga
          </Button>
          <Button component={Link} to="/" variant="text" sx={{ color: '#FFD700' }}>
            Strona główna
          </Button>
        </Stack>
      </Box>
    </>
  );
};

export default ArticlePage;
